import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ArrowDown, ArrowUp, ArrowUpDown, ChevronLeft, ChevronRight, Copy, Download, Loader2, Search, X } from 'lucide-react';
import { toast } from 'sonner';
import { formatDate } from '@/lib/utils';
import { getImportableTransmitters, importTransmittersToAccount } from '../api';

interface ImportTransmittersModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  accountId: string;
  accountName: string;
}

type SortKey = 'devId' | 'status' | 'lastpacketAt';
type SortDir = 1 | -1;
const PAGE_SIZE = 50;

export default function ImportTransmittersModal({ open, onOpenChange, accountId, accountName }: ImportTransmittersModalProps) {
  const queryClient = useQueryClient();
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [sortKey, setSortKey] = useState<SortKey | null>(null);
  const [sortDir, setSortDir] = useState<SortDir>(1);
  const [page, setPage] = useState(1);

  const { data: all, isLoading } = useQuery({
    queryKey: ['importable-transmitters', accountId],
    queryFn: () => getImportableTransmitters(accountId),
    enabled: open,
  });

  const importable = useMemo(() => (all || []).filter((t) => !t.alreadyImported), [all]);

  const filtered = useMemo(() => {
    let result = importable;
    if (statusFilter !== 'all') {
      const isActive = statusFilter === 'active';
      result = result.filter((t) => t.activeFlag === isActive);
    }
    if (search) {
      const lower = search.toLowerCase();
      result = result.filter((t) =>
        t.devId.toLowerCase().includes(lower) ||
        (t.name || '').toLowerCase().includes(lower) ||
        (t.deviceType || '').toLowerCase().includes(lower),
      );
    }
    return result;
  }, [importable, search, statusFilter]);

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) setSortDir((d) => (d === 1 ? -1 : 1));
    else { setSortKey(key); setSortDir(1); }
    setPage(1);
  };

  const sorted = useMemo(() => {
    if (!sortKey) return filtered;
    return [...filtered].sort((a, b) => {
      let cmp = 0;
      switch (sortKey) {
        case 'devId': cmp = a.devId.localeCompare(b.devId); break;
        case 'status': cmp = (a.activeFlag === b.activeFlag ? 0 : a.activeFlag ? -1 : 1); break;
        case 'lastpacketAt': cmp = (a.lastpacketAt || '').localeCompare(b.lastpacketAt || ''); break;
      }
      return cmp * sortDir;
    });
  }, [filtered, sortKey, sortDir]);

  const totalPages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const paged = sorted.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  const importMutation = useMutation({
    mutationFn: (ids: string[]) => importTransmittersToAccount(accountId, ids),
    onSuccess: (result) => {
      const total = result.imported + result.reassigned;
      toast.success(`Imported ${total} transmitter${total !== 1 ? 's' : ''}`);
      if (result.errors.length > 0) toast.warning(`${result.errors.length} transmitter(s) had errors`, { description: result.errors.slice(0, 3).join('\n') });
      queryClient.invalidateQueries({ queryKey: ['account-transmitters', accountId] });
      queryClient.invalidateQueries({ queryKey: ['account-dashboard', accountId] });
      queryClient.invalidateQueries({ queryKey: ['importable-transmitters', accountId] });
      setSelected(new Set());
      onOpenChange(false);
    },
    onError: () => toast.error('Failed to import transmitters'),
  });

  const toggleAll = () => setSelected(selected.size === sorted.length ? new Set() : new Set(sorted.map((t) => t._id)));
  const toggleOne = (id: string) => { const n = new Set(selected); n.has(id) ? n.delete(id) : n.add(id); setSelected(n); };

  const SortIcon = ({ col }: { col: SortKey }) => {
    if (sortKey !== col) return <ArrowUpDown className="ml-1 inline h-3.5 w-3.5 text-muted-foreground/50" />;
    return sortDir === 1 ? <ArrowUp className="ml-1 inline h-3.5 w-3.5" /> : <ArrowDown className="ml-1 inline h-3.5 w-3.5" />;
  };

  const resetState = () => { setSelected(new Set()); setSearch(''); setStatusFilter('all'); setSortKey(null); setSortDir(1); setPage(1); };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) resetState(); onOpenChange(v); }}>
      <DialogContent className="max-w-4xl max-h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Import Transmitters</DialogTitle>
          <DialogDescription>Select transmitters from the legacy organisation to import into <span className="font-medium text-foreground">{accountName}</span></DialogDescription>
        </DialogHeader>

        <div className="flex items-center gap-2 px-1">
          <Search className="h-4 w-4 text-muted-foreground shrink-0" />
          <Input placeholder="Search by dev ID, name, or device type..." value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} className="h-8" />
          {search && <button onClick={() => { setSearch(''); setPage(1); }} className="text-muted-foreground hover:text-foreground"><X className="h-4 w-4" /></button>}
          <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(1); }}>
            <SelectTrigger className="h-8 w-[150px] shrink-0">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="inactive">Inactive</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="flex-1 overflow-y-auto border rounded-md">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-10"><Checkbox checked={sorted.length > 0 && selected.size === sorted.length} onCheckedChange={toggleAll} disabled={sorted.length === 0} /></TableHead>
                <TableHead className="cursor-pointer select-none" onClick={() => toggleSort('devId')}>Dev ID<SortIcon col="devId" /></TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Device Type</TableHead>
                <TableHead>Legacy Org ID</TableHead>
                <TableHead className="cursor-pointer select-none" onClick={() => toggleSort('status')}>Status<SortIcon col="status" /></TableHead>
                <TableHead className="cursor-pointer select-none" onClick={() => toggleSort('lastpacketAt')}>Last Packet<SortIcon col="lastpacketAt" /></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading && Array.from({ length: 5 }).map((_, i) => (
                <TableRow key={i}>{Array.from({ length: 7 }).map((__, j) => <TableCell key={j}><Skeleton className="h-4 w-16" /></TableCell>)}</TableRow>
              ))}
              {!isLoading && sorted.length === 0 && (
                <TableRow><TableCell colSpan={7} className="py-8 text-center text-muted-foreground">
                  {importable.length === 0 ? 'All transmitters from this organisation are already imported' : 'No matching transmitters'}
                </TableCell></TableRow>
              )}
              {!isLoading && paged.map((t) => (
                <TableRow key={t._id} className="cursor-pointer hover:bg-muted/50" onClick={() => toggleOne(t._id)}>
                  <TableCell><Checkbox checked={selected.has(t._id)} onCheckedChange={() => toggleOne(t._id)} /></TableCell>
                  <TableCell className="font-medium">
                    <span className="inline-flex items-center gap-1">
                      {t.devId}
                      <button
                        onClick={(e) => { e.stopPropagation(); navigator.clipboard.writeText(t.devId); toast.success('Copied dev ID'); }}
                        className="text-muted-foreground/50 hover:text-foreground transition-colors"
                      >
                        <Copy className="h-3.5 w-3.5" />
                      </button>
                    </span>
                  </TableCell>
                  <TableCell className="max-w-[200px] truncate">{t.name || '—'}</TableCell>
                  <TableCell>{t.deviceType || '—'}</TableCell>
                  <TableCell className="font-mono text-xs text-muted-foreground">{t.organisationId || '—'}</TableCell>
                  <TableCell><Badge variant={t.activeFlag ? 'success' : 'secondary'}>{t.activeFlag ? 'Active' : 'Inactive'}</Badge></TableCell>
                  <TableCell>{t.lastpacketAt ? formatDate(t.lastpacketAt) : '—'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        <DialogFooter className="flex items-center justify-between sm:justify-between">
          <p className="text-sm text-muted-foreground">{selected.size} of {importable.length} selected</p>
          {totalPages > 1 && (
            <div className="flex items-center gap-1">
              <Button variant="outline" size="icon" className="h-7 w-7" onClick={() => setPage((p) => p - 1)} disabled={safePage <= 1}>
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="text-sm text-muted-foreground px-2">{safePage} / {totalPages}</span>
              <Button variant="outline" size="icon" className="h-7 w-7" onClick={() => setPage((p) => p + 1)} disabled={safePage >= totalPages}>
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          )}
          <Button onClick={() => importMutation.mutate(Array.from(selected))} disabled={selected.size === 0 || importMutation.isPending}>
            {importMutation.isPending ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Download className="mr-1.5 h-4 w-4" />}
            {importMutation.isPending ? 'Importing...' : `Import ${selected.size} Transmitter${selected.size !== 1 ? 's' : ''}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
